import { useState } from 'react';
import { useOpenItems, type OpenItem } from '@/api/hooks/useOpenItems';
import { useSendOpenItemBatch } from '@/api/hooks/useSendOpenItemBatch';
import { OpenItemGroups } from './OpenItemGroups';
import { SendBatchBar } from './SendBatchBar';

export function AccountantOpenItemsPage({ grantId, taxYear }: { grantId: string; taxYear: number }) {
  const query = useOpenItems(grantId, taxYear);
  const sendBatch = useSendOpenItemBatch();
  const [selected, setSelected] = useState<string[]>([]);
  const items = query.data?.items ?? [];
  const selectedItems = items.filter((item) => selected.includes(item.open_item_id));

  function toggle(item: OpenItem) {
    setSelected((current) => current.includes(item.open_item_id) ? current.filter((id) => id !== item.open_item_id) : [...current, item.open_item_id]);
  }

  function send() {
    if (selectedItems.length === 0) return;
    sendBatch.mutate({
      grantId,
      taxYear,
      openItemIds: selectedItems.map((item) => item.open_item_id),
      expectedVersions: selectedItems.map((item) => item.version),
      messages: selectedItems.map((item) => item.message),
      idempotencyKey: crypto.randomUUID(),
    });
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-6" aria-labelledby="accountant-open-items-heading">
      <div>
        <h2 id="accountant-open-items-heading" tabIndex={-1} className="text-title font-semibold">Open items</h2>
        <p className="text-sm text-muted-foreground">Questions for the owner · {taxYear}</p>
      </div>
      {query.isLoading && <p role="status">Loading open items…</p>}
      {query.isError && <p role="alert">Open items are unavailable. Try again.</p>}
      {!query.isLoading && !query.isError && items.length === 0 && <p role="status">Nothing open. Gaps generate items automatically.</p>}
      {!query.isLoading && !query.isError && items.length > 0 && <OpenItemGroups items={items} selectedIds={selected} onToggle={toggle} />}
      <SendBatchBar draftCount={query.data?.counts.draft ?? 0} selectedCount={selected.length} disabled={sendBatch.isPending} onSend={send} />
    </div>
  );
}
